'use client'

/**
 * The one chart component.
 *
 * Every price chart on the site renders through this: collections, markets,
 * alkanes. It wraps TradingView Advanced Charts, which replaces the five
 * separate lightweight-charts implementations that had accumulated — each with
 * its own fetching, bucketing, theming and range handling, each drifting from
 * the others whenever one was fixed.
 *
 * What the upgrade actually buys, beyond consistency:
 *
 *   * **Real candles at real resolutions.** 5m through 1W, backed by an API
 *     that computes OHLC rather than a client that invents it.
 *   * **Deep history without loading it all.** The datafeed pages backwards as
 *     the user scrolls, so opening a chart costs one screen of bars rather
 *     than a collection's entire lifetime.
 *   * **The trader toolkit** — drawing tools, indicators, measurement,
 *     crosshair sync — none of which lightweight-charts has.
 *
 * The library is vendored under `public/charting_library` and loaded as a
 * plain script rather than imported. It is a ~2MB UMD bundle with its own
 * lazy-loaded chunk graph; bundling it through webpack would both bloat the
 * app chunk and break its runtime chunk resolution.
 */

import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { useTheme } from 'next-themes'
import classNames from 'classnames'
import {
	createDatafeed,
	type Bar,
	type ChartMark,
	type ChartOutlier,
	type OhlcvBar
} from './datafeed'
import {
	buildSymbol,
	parseSymbol,
	resolutionSeconds,
	SUPPORTED_RESOLUTIONS,
	DEFAULT_RESOLUTION,
	withDenom,
	type ChartKind,
	type Denom
} from './symbol'
import { chartOverrides, LOADING_SCREEN, surfaceColor, tokenColor, TV_CSS_URL } from './theme'
import { renderSwitcher } from './header-switcher'
import { chartLog, debugEnabled, nextChartId, useDepChangeLog } from './debug'
import { clearTrend, putTrend, trendPoints, trendRevision } from './fair-band'
import { clusterPoints, clusterRadius } from './cluster'
import { compactPrice, formatBtc, formatForDenom, unitForSats, type PriceUnit } from './price-format'
import isFungible from '@/utils/is-fungible'
import { useBtcUsd } from '@/lib/quotes/hooks'
import { forgetUnit, unitFor } from './units'
import ChartOverlayToggles from './overlay-toggles'
import { CDN_PREVIEW_URL } from '@/utils/constants'
import { useRouter } from 'next/navigation'
import type { ChartViolation } from './datafeed'
import type { TwetchPost, TwetchUser } from '@/app/alkanes/lib/twetch-api'
import { pixelArtClass, resolveTwetchImage } from '@/app/alkanes/lib/twetch-media'
import { openTwetchUserProfile } from '@/app/alkanes/lib/twetch-profile'

const SCRIPT_SRC = '/charting_library/charting_library.standalone.js'

/** Header labels for each denomination. `Mcap` rather than `Market cap` so the
 * switcher stays one line in the toolbar, matching how every DEX terminal
 * abbreviates it. */
const DENOM_LABELS: Record<Denom, string> = {
	sats: 'BTC',
	usd: 'USD',
	mcap: 'Mcap'
}

/** How many bars to show when a chart first lands on its data. */
const VISIBLE_BARS_ON_LOAD = 180

/**
 * Remembered chart preferences.
 *
 * Denomination and candle width are a reading habit, not a per-visit decision:
 * someone who works in market cap on daily candles wants that chart every time,
 * not a fresh default to re-set on each page.
 *
 * Stored per browser, not per asset — the preference is about how you read a
 * chart, not about which asset you are looking at. Wrapped because storage
 * throws in private mode and a chart must not fail over a preference.
 */
const PREF_PREFIX = 'owChart.'

function rememberPreference(key: string, value: string) {
	try {
		window.localStorage.setItem(`${PREF_PREFIX}${key}`, value)
	} catch {
		// Private mode or blocked storage; the chart works, it just forgets.
	}
}

function recallPreference(key: string): string | null {
	try {
		return window.localStorage.getItem(`${PREF_PREFIX}${key}`)
	} catch {
		return null
	}
}

/** Module-level so a page with several charts loads the bundle once. */
let scriptPromise: Promise<void> | null = null

function loadTradingView(): Promise<void> {
	if (typeof window === 'undefined') {
		return Promise.resolve()
	}
	if ((window as { TradingView?: unknown }).TradingView) {
		return Promise.resolve()
	}
	if (scriptPromise) {
		return scriptPromise
	}
	scriptPromise = new Promise<void>((resolve, reject) => {
		const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`)
		if (existing) {
			existing.addEventListener('load', () => resolve())
			existing.addEventListener('error', () => reject(new Error('chart bundle failed')))
			return
		}
		const script = document.createElement('script')
		script.src = SCRIPT_SRC
		script.async = true
		script.onload = () => resolve()
		script.onerror = () => {
			// Let a later mount retry rather than caching the failure forever.
			scriptPromise = null
			reject(new Error('chart bundle failed'))
		}
		document.head.appendChild(script)
	})
	return scriptPromise
}

/**
 * The slice of TradingView's widget API this component uses.
 *
 * Typed structurally rather than against the shipped `.d.ts` because the
 * bundle is loaded as a global script, not imported — so there is no module to
 * take types from at the call site. Every method is optional: the library's
 * surface shifts between versions, and a chart that silently loses its hover
 * readout is better than one that throws during construction.
 */
interface TvSubscription {
	subscribe?: (obj: unknown, handler: (payload: never) => void) => void
}
interface TvExecutionShape {
	setText?: (t: string) => TvExecutionShape
	setTooltip?: (t: string) => TvExecutionShape
	setTextColor?: (c: string) => TvExecutionShape
	setArrowColor?: (c: string) => TvExecutionShape
	setDirection?: (d: 'buy' | 'sell') => TvExecutionShape
	setTime?: (t: number) => TvExecutionShape
	setPrice?: (p: number) => TvExecutionShape
	remove?: () => void
}
interface TvActiveChart {
	getVisibleRange?: () => { from: number; to: number } | undefined
	getTimeScale?: () => TvTimeScale

	crossHairMoved?: () => TvSubscription
	onIntervalChanged?: () => TvSubscription
	onVisibleRangeChanged?: () => TvSubscription
	setVisibleRange?: (range: { from: number; to: number }) => Promise<void> | void
	createExecutionShape?: () => TvExecutionShape
	getPanes?: () => TvPane[]
}
interface TvPane {
	getMainSourcePriceScale?: () => TvPriceScale | null
}
interface TvTimeScale {
	/** Unix seconds for a pixel x on the plot, or null off the data. */
	coordinateToTime: (x: number) => number | null
	/** Plot width in pixels. */
	width: () => number
	/** Pixels per bar. */
	barSpacing: () => number
	/** Whitespace to the right of the last bar, in bars. */
	rightOffset: () => number
}
interface TvPriceScale {
	/** Pixel y for a price, relative to the pane. */
	priceToCoordinate: (price: number) => number | null
}
interface TvWidget {
	remove: () => void
	/** Change symbol and/or resolution WITHOUT rebuilding the widget. */
	setSymbol?: (symbol: string, interval: string, onReady?: () => void) => void
	onChartReady?: (cb: () => void) => void
	activeChart?: () => TvActiveChart | undefined
	headerReady?: () => Promise<void>
	createButton?: (options?: {
		align?: 'left' | 'right'
		useTradingViewStyle?: boolean
	}) => HTMLElement
}

/** Render a crosshair price the way the axis does, so the header a parent
 * shows and the axis the user is reading agree. */
function formatAxisPrice(price: number, denom: Denom, unit: PriceUnit = 'sats'): string {
	if (denom === 'sats') {
		if (unit === 'btc') {
			return `${formatBtc(price)} BTC`
		}
		// A sub-sat unit price (a TAP token) keeps its digits; a whole-sat
		// price reads as a count.
		return price < 1
			? `${formatForDenom(price, 'sats')} sats`
			: `${Math.round(price).toLocaleString('en-US')} sats`
	}
	return price.toLocaleString('en-US', {
		style: 'currency',
		currency: 'USD',
		maximumFractionDigits: price < 100 ? 2 : 0
	})
}

/**
 * An axis or legend label: the compact number with its unit after it. The
 * hover label (`formatAxisPrice`) spells sats out in full; the axis cannot
 * afford the width.
 */
function formatAxisLabel(price: number, denom: Denom, unit: PriceUnit = 'sats'): string {
	if (!Number.isFinite(price)) {
		return ''
	}
	if (denom === 'sats') {
		return unit === 'btc' ? `${formatBtc(price)} BTC` : `${formatForDenom(price, 'sats')} sats`
	}
	return `$${formatForDenom(price, denom)}`
}

/** A human label for a visible span, matching the range tabs users already
 * know from the old chart. */
function spanLabel(seconds: number): string {
	const days = seconds / 86400
	if (days <= 1.5) {
		return '1D'
	}
	if (days <= 9) {
		return '1W'
	}
	if (days <= 45) {
		return '1M'
	}
	if (days <= 135) {
		return '3M'
	}
	if (days <= 420) {
		return '1Y'
	}
	return 'ALL'
}

/**
 * Percent change across the visible window, computed from the bars actually
 * on screen.
 *
 * Deriving it from the drawn series rather than from a separate stats call is
 * deliberate: the old chart could show a header change that disagreed with the
 * line beneath it, because the two came from different queries.
 */
function rangeChange(
	bars: Bar[],
	fromSec: number,
	toSec: number
): { label: string; percent: number } | null {
	const from = fromSec * 1000
	const to = toSec * 1000
	const visible = bars.filter((b) => b.time >= from && b.time <= to)
	if (visible.length < 2) {
		return null
	}
	const open = visible[0].open
	const close = visible[visible.length - 1].close
	if (!Number.isFinite(open) || open <= 0) {
		return null
	}
	return {
		label: spanLabel(toSec - fromSec),
		percent: ((close - open) / open) * 100
	}
}

export interface TvChartProps {
	kind: ChartKind
	/** Collection slug, market symbol, or alkane id. */
	id: string
	/**
	 * Human name for the asset.
	 *
	 * Feeds TradingView's legend, which is the one label that stays on screen
	 * while you read the chart — without it the legend prints the raw id (`2:0`
	 * for an alkane), which is an address rather than a name.
	 *
	 * It is drawn as a visible heading ONLY in compact mode, where the header
	 * and legend are disabled and there would otherwise be nothing naming the
	 * series. In the full layout the legend already says it, and a heading
	 * would be a second copy.
	 */
	title?: string
	/** Starting denomination. */
	denom?: Denom
	/** Which denominations the user may switch to. Empty hides the toggle. */
	denoms?: Denom[]
	/**
	 * Override the switcher's labels.
	 *
	 * The same denomination reads differently depending on the asset. On an
	 * alkane the dollar series IS the price, so the switcher should say
	 * `Price / Mcap`; on a collection the choice is between satoshis and
	 * dollars and `Price` would say nothing. Defaults suit collections.
	 */
	denomLabels?: Partial<Record<Denom, string>>
	/** Starting resolution, in TradingView's vocabulary. */
	resolution?: string
	/** Social markers on the time axis. */
	getMarks?: (symbol: string, from: number, to: number) => Promise<ChartMark[]>
	/** Compact mode: hides the toolbars for embedding in a card. */
	compact?: boolean
	/** Reports the crosshair price as the user hovers, so a page header can
	 * re-point its big number to whatever the cursor is over. `null` when the
	 * cursor leaves the plot. */
	onHoverPrice?: (label: string | null) => void
	/** A circulating supply the page already knows, in whole tokens, for a
	 * market-cap series the server may not be able to price on its own. */
	supplyHint?: number
	/** See `DatafeedOptions.resolveSupplyHint`. */
	resolveSupplyHint?: () => Promise<number | undefined>
	/** See `DatafeedOptions.typicalPrice`. */
	typicalPrice?: (denom: Denom) => number | undefined
	/** Posts about this asset, drawn as their authors' faces at the time
	 * they were posted. Any chart with a feed can supply them. */
	theses?: ThesisMarker[]
	/** Reports the visible window and its percent change, so a header's
	 * "change" stat can never disagree with the picture on screen. */
	onRangeChange?: (label: string, percentChange: number) => void
	/** The viewer's own fills, drawn on the plot as buy/sell arrows.
	 *
	 * Real trades only — callers build these from broadcast records and
	 * chain-decoded history, never from anything invented. An arrow on a price
	 * chart is a claim that the viewer traded there.
	 */
	executions?: ChartExecution[]
	/**
	 * Bars supplied by the caller instead of fetched.
	 *
	 * For pages that already hold their series — the alkanes terminal fetches
	 * its own, with a USD-vs-pool fallback and market-cap scaling this
	 * component has no business re-deriving. Passing bars turns off every
	 * fetch path, including the degraded fallback and realtime polling: the
	 * caller owns what exists and when it changes.
	 */
	bars?: OhlcvBar[]
	/**
	 * Fires when the viewer changes denomination.
	 *
	 * Pages that supply their own bars need this: the alkanes terminal scales
	 * its series into market cap before handing it over, so it has to know
	 * which axis the viewer picked. Pages that let the chart fetch do not —
	 * denomination is part of the symbol and the datafeed handles it.
	 */
	onDenomChange?: (denom: Denom) => void
	/**
	 * Fires when the viewer picks a different resolution in the toolbar.
	 *
	 * Pages that supply their own bars need this. The chart can aggregate the
	 * series it was given into coarser buckets, but it cannot invent finer ones
	 * or reach further back than the data it holds — so a page whose feed is
	 * range-based has to widen its own fetch when the viewer asks for a longer
	 * candle. Without it, changing resolution appears to do nothing.
	 */
	onResolutionChange?: (resolution: string) => void
	/**
	 * Draw the page's own elements on top of the plot, positioned in time.
	 *
	 * TradingView's `getMarks` can only render a letter inside a coloured
	 * circle, which is not enough for social: a thesis is a person, and a face
	 * is how you recognise one. So a page that wants avatars gets the geometry
	 * and renders its own DOM, exactly as the lightweight-charts version did.
	 *
	 * `timeToX` returns null for a timestamp outside the visible window, so
	 * callers can skip rather than draw something clipped off-screen.
	 */
	overlay?: (geometry: ChartGeometry) => React.ReactNode
	className?: string
}

/** Where the plot is, and how to place a timestamp inside it. */
export interface ChartGeometry {
	/** Pixel x for a unix-seconds timestamp, relative to the chart container.
	 * Null when the timestamp is outside the visible range. */
	timeToX: (ts: number) => number | null
	/** Pixel y for a price, relative to the chart container. Null when the
	 * price scale is unavailable or the price falls outside it. */
	priceToY: (price: number) => number | null
	/** The plot rectangle, relative to the chart container. */
	plot: { left: number; top: number; width: number; height: number }
	/** Visible window, unix seconds. */
	from: number
	to: number
}

/** One of the viewer's own fills. */
export interface ChartExecution {
	/** Unix seconds. */
	ts: number
	side: 'buy' | 'sell'
	/** Optional fill price. When absent the arrow is pinned to the bar's close,
	 * which is the honest placement for a fill whose price we did not record. */
	price?: number
	/** Hover text, e.g. the amount swapped. */
	tooltip?: string
}

/**
 * The fair-value band, drawn over the plot.
 *
 * Candles answer "what printed". For a collection of non-identical items that
 * is a harder number to read than it looks: in a quiet hour one sale sets
 * open, high, low and close at once, and whether that sale was a floor piece
 * or a grail decides the whole bar. A year of that is a chart made of
 * disconnected marks with gaps between them.
 *
 * The band is the same tape read as a distribution instead of as a sequence,
 * and it is defined on EVERY bucket -- a trailing window always has something
 * in it -- which is what closes the gaps.
 *
 * Positioned through the library's own `priceToCoordinate`, so it follows
 * zoom, autoscale and log scale exactly as the candles do.
 */
function FairBand({
	geometry,
	symbol,
	denom
}: {
	geometry: ChartGeometry
	symbol: string
	denom: Denom
}) {
	const unit = unitFor(symbol)
	const points = trendPoints(symbol)
	if (points.length < 2) {
		return null
	}

	// Clip to the visible window plus one point either side, so the shaded
	// region runs to the edges of the plot instead of stopping at the last
	// point that happened to be inside it.
	const inView: { x: number; lo: number; hi: number; fair: number }[] = []
	for (const p of points) {
		const x = geometry.timeToX(p.time)
		if (x === null) {
			continue
		}
		const lo = geometry.priceToY(p.p10)
		// The drawn upper edge is capped at BAND_DRAW_CEILING x fair. The high
		// decile is real -- grails do trade at eight times the median -- but a
		// shaded region that shoots off the top of the plot every time one
		// enters the window says nothing a reader can use. The readout under
		// the legend still shows the true p90.
		const hi = geometry.priceToY(Math.min(p.p90, p.fair * BAND_DRAW_CEILING))
		const fair = geometry.priceToY(p.fair)
		if (lo === null || hi === null || fair === null) {
			continue
		}
		inView.push({ x, lo, hi, fair })
	}
	if (inView.length < 2) {
		return null
	}

	const line = (key: 'lo' | 'hi') =>
		inView.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p[key].toFixed(1)}`).join(' ')
	// Down the high decile and back along the low one: one closed path, so the
	// fill is a region rather than a stack of vertical slivers.
	const area = `${line('hi')} ${inView
		.slice()
		.reverse()
		.map((p) => `L${p.x.toFixed(1)},${p.lo.toFixed(1)}`)
		.join(' ')} Z`

	const accent = tokenColor('--primary-500', '#9E77ED')
	const lastPoint = points[points.length - 1]

	return (
		<svg
			className="pointer-events-none absolute inset-0 z-[5]"
			width="100%"
			height="100%"
			aria-label="Fair value band"
		>
			<path d={area} fill={accent} fillOpacity={0.1} />
			<path d={line('hi')} stroke={accent} strokeOpacity={0.35} strokeWidth={1} fill="none" />
			<path d={line('lo')} stroke={accent} strokeOpacity={0.35} strokeWidth={1} fill="none" />
			{/* No fair stroke: the candles ARE the fair line now (the server's
			    default series traces it), so a second copy on top of the
			    closes is clutter. The band is the context around them. */}
			<title>{`Fair value ${formatForDenom(lastPoint.fair, denom, unit)} — band ${formatForDenom(
				lastPoint.p10,
				denom,
				unit
			)} to ${formatForDenom(lastPoint.p90, denom, unit)} over ${lastPoint.samples} trades`}</title>
		</svg>
	)
}

/** A thesis: a post about the asset, and who wrote it. */
export type ThesisMarker = { post: TwetchPost; user?: TwetchUser }

/**
 * What the chart shows on hover: one card, positioned by the overlay that
 * asked for it. The browser's own `title` tooltip is slow to appear, cannot
 * be styled and cannot hold a face, so overlays hand their content here and
 * the chart draws it beside the cursor.
 */
export type HoverCard = { x: number; y: number; body: React.ReactNode }

/** The one card's element id, so every trigger can point at it. */
const HOVER_CARD_ID = 'tv-chart-hover-card'

function formatWhen(secs: number): string {
	const d = new Date(secs * 1000)
	const thisYear = d.getUTCFullYear() === new Date().getUTCFullYear()
	return d.toLocaleString('en-US', {
		month: 'short',
		day: 'numeric',
		...(thisYear ? {} : { year: 'numeric' }),
		hour: '2-digit',
		minute: '2-digit',
		timeZone: 'UTC',
		hour12: false
	})
}

/** `<txid>i<n>` shortened for a label. */
function shortInscription(id: string): string {
	return id.length > 14 ? `${id.slice(0, 6)}…${id.slice(-6)}` : id
}

/** The card, placed by measurement: above its anchor when there is room,
 * below it when there is not, and never past the chart's edges. */
function HoverCardView({
	card,
	plot,
	width,
	onEnter,
	onLeave
}: {
	card: HoverCard
	plot: ChartGeometry['plot']
	width: number
	/** The pointer reached the card: keep it open. */
	onEnter: () => void
	/** The pointer left the card: let it close. */
	onLeave: () => void
}) {
	const ref = useRef<HTMLDivElement>(null)
	const [size, setSize] = useState<{ w: number; h: number } | null>(null)
	// Measured before paint, so the card never shows a frame at the wrong
	// place.
	useLayoutEffect(() => {
		const el = ref.current
		if (!el) {
			return
		}
		const r = el.getBoundingClientRect()
		setSize((prev) =>
			prev && prev.w === r.width && prev.h === r.height ? prev : { w: r.width, h: r.height }
		)
	}, [card])
	const w = size?.w ?? 0
	const h = size?.h ?? 0
	const fitsAbove = card.y - 10 - h >= plot.top + 8
	// Inside the plot, not the container: the drawing toolbar sits to the
	// left of the plot and a card over it hides the tools.
	const minLeft = plot.left + w / 2 + 8
	const left = Math.min(Math.max(card.x, minLeft), Math.max(minLeft, width - w / 2 - 8))
	const top = fitsAbove ? card.y - 10 - h : card.y + 12
	return (
		<div
			ref={ref}
			id={HOVER_CARD_ID}
			role="tooltip"
			onMouseEnter={onEnter}
			onMouseLeave={onLeave}
			className="pointer-events-auto absolute z-40 w-max max-w-[280px] rounded-md border border-border bg-popover px-2.5 py-2 text-xs text-popover-foreground shadow-md"
			style={{
				left,
				top,
				transform: 'translateX(-50%)',
				opacity: size ? 1 : 0
			}}
		>
			{card.body}
		</div>
	)
}

/** How far above fair the shaded band is drawn, as a multiple. */
const BAND_DRAW_CEILING = 1.5

/** What each overlay is, for the checkbox under the chart. One line each. */
export const OVERLAY_HINTS = {
	band: 'Fair value from recent sales. The line is the mark; the shading is the range most pieces clear in.',
	sales: 'Each sale at the price it cleared. Nearby sales merge into one dot.',
	theses: 'Posts about this collection, on the bar they were written.'
}

/** Longest thesis tooltip before it is cut: a mark points at a post. */
const THESIS_TEXT_LIMIT = 280

/**
 * The prints: every sale, drawn where it cleared.
 *
 * The candles trace the mark and the band shows the spread; the prints are
 * the sales themselves, and they sit at their own price so a viewer can see
 * what the mark is made of. A sale the fence kept out of the candles is a
 * hollow ring rather than a filled dot.
 *
 * Sales outside the visible price range are counted per pixel column into
 * one chevron with a number, pinned to the edge they are beyond. Sales
 * within a few pixels of each other are one dot, sized by how many it holds
 * (see `cluster.ts`); zooming in separates them again.
 *
 * The card leads with what the piece sold for, in its own unit, whatever
 * the axis is showing: a sale is never headlined as an implied market cap.
 * An NFT card shows the item; a token card shows the lot. A single sale
 * opens the inscription (NFTs); a cluster zooms the chart to its span. On a
 * touch screen the first tap opens the card instead.
 */
/** Dots whose centres are within this many pixels merge. */
const CLUSTER_RADIUS = 7

/** A pointer that cannot hover: the first tap must show the card. */
function coarsePointer(): boolean {
	try {
		return window.matchMedia('(pointer: coarse)').matches
	} catch {
		return false
	}
}

/** Sats as `0.0123 BTC` or `7.08K sats`, plus dollars when bitcoin is known. */
function PriceLine({
	sats,
	fallback,
	denom,
	unit,
	btcUsd
}: {
	sats: number | undefined
	fallback: number
	denom: Denom
	unit: PriceUnit
	btcUsd: number | null
}) {
	const native = sats !== undefined ? formatAxisLabel(sats, 'sats', unit) : formatAxisLabel(fallback, denom, unit)
	const usd = sats !== undefined && btcUsd ? formatAxisLabel((sats / 1e8) * btcUsd, 'usd') : null
	return (
		<div className="flex items-baseline gap-2">
			<span className="text-sm font-semibold tabular-nums text-foreground">{native}</span>
			{usd && <span className="tabular-nums text-muted-foreground">{usd}</span>}
		</div>
	)
}

function DeltaLine({ delta, label }: { delta: number | null; label?: string }) {
	if (delta === null) {
		return null
	}
	return (
		<div className="tabular-nums text-muted-foreground">
			{label ? `${label} ` : ''}
			<span className={Math.abs(delta) > 0.005 ? 'text-foreground' : undefined}>
				{delta >= 0 ? '+' : ''}
				{(delta * 100).toFixed(1)}%
			</span>{' '}
			vs fair
		</div>
	)
}

function PrintDots({
	geometry,
	prints,
	outliers,
	denom,
	unit,
	fungible,
	btcUsd,
	fairAt,
	onHover,
	onOpen,
	onZoom,
	bucketSecs
}: {
	geometry: ChartGeometry
	prints: ChartOutlier[]
	outliers: ChartOutlier[]
	denom: Denom
	unit: PriceUnit
	/** A token: a sale is a lot, and the transfer inscription is not the item. */
	fungible: boolean
	btcUsd: number | null
	/** The fair value at a time, in the chart's denomination, if known. */
	fairAt: (time: number) => number | null
	onHover: (card: HoverCard | null) => void
	onOpen: (inscription: string) => void
	/** Bring a span of time into view. */
	onZoom: (from: number, to: number) => void
	/** The chart's bar width in seconds, so a zoom lands on whole bars. */
	bucketSecs: number
}) {
	const top = geometry.plot.top + 4
	const bottom = geometry.plot.top + geometry.plot.height - 4
	type Placed = {
		key: string
		x: number
		y: number
		pinned: 'above' | 'below' | null
		fenced: boolean
		sale: ChartOutlier
	}
	const place = (o: ChartOutlier, fenced: boolean): Placed | null => {
		const x = geometry.timeToX(o.time)
		if (x === null) {
			return null
		}
		const y = geometry.priceToY(o.price)
		if (y === null) {
			return null
		}
		const pinned = y < top ? 'above' : y > bottom ? 'below' : null
		return {
			key: `${fenced ? 'f' : 'k'}-${o.time}-${o.price}`,
			x,
			y: Math.min(Math.max(y, top), bottom),
			pinned,
			fenced,
			sale: o
		}
	}
	const placed = [
		...prints.map((o) => place(o, false)),
		...outliers.map((o) => place(o, true))
	].filter((d): d is Placed => d !== null)
	if (placed.length === 0) {
		return null
	}

	const deltaFor = (sale: ChartOutlier, price = sale.price) => {
		const fair = fairAt(sale.time)
		return fair && fair > 0 ? price / fair - 1 : null
	}
	const lotLine = (sale: ChartOutlier) => {
		if (!fungible || !sale.amount || !(sale.amount > 0)) {
			return null
		}
		const total = sale.price_sats !== undefined ? sale.price_sats * sale.amount : null
		return (
			<div className="tabular-nums text-muted-foreground">
				Lot {compactPrice(sale.amount)}
				{total !== null && ` · ${formatAxisLabel(total, 'sats', unitForSats(total))} total`}
			</div>
		)
	}
	// Only when the headline is the sale itself; before the server carries
	// sats the headline already is the axis value and this would repeat it.
	const impliedLine = (price: number, sats: number | undefined) =>
		denom === 'mcap' && sats !== undefined ? (
			<div className="tabular-nums text-muted-foreground">Implied cap {formatAxisLabel(price, 'mcap')}</div>
		) : null

	const cardFor = (d: Placed): HoverCard => {
		const id = fungible ? undefined : d.sale.inscription
		return {
			x: d.x,
			y: d.y,
			body: (
				<div className="flex gap-2.5">
					{id && (
						// eslint-disable-next-line @next/next/no-img-element
						<img
							src={`${CDN_PREVIEW_URL}/${id}`}
							alt=""
							loading="lazy"
							onError={(e) => {
								e.currentTarget.style.display = 'none'
							}}
							className="h-8 w-8 shrink-0 rounded bg-muted object-cover"
						/>
					)}
					<div className="min-w-0 space-y-0.5">
						{d.fenced && <div className="text-muted-foreground">Not in the candles</div>}
						<PriceLine sats={d.sale.price_sats} fallback={d.sale.price} denom={denom} unit={unit} btcUsd={btcUsd} />
						{lotLine(d.sale)}
						<DeltaLine delta={deltaFor(d.sale)} />
						{impliedLine(d.sale.price, d.sale.price_sats)}
						<div className="text-muted-foreground">
							{formatWhen(d.sale.time)} UTC
							{id && <span className="font-mono"> · {shortInscription(id)}</span>}
							{d.pinned && ` · ${d.pinned === 'above' ? 'above' : 'below'} the view`}
						</div>
						{id && (
							<a
								href={`/inscription/${id}`}
								className="inline-block pt-0.5 font-medium text-[var(--primary-700)] hover:underline dark:text-[var(--primary-500)]"
							>
								Open inscription ↗
							</a>
						)}
					</div>
				</div>
			)
		}
	}

	// Sales within reach of each other become one dot. Kept and excluded
	// sales cluster separately: a hollow ring means something different
	// from a filled dot and the two must not average into one.
	const visible = placed.filter((d) => !d.pinned)
	const clusters = [
		...clusterPoints(
			visible.filter((d) => !d.fenced),
			CLUSTER_RADIUS
		),
		...clusterPoints(
			visible.filter((d) => d.fenced),
			CLUSTER_RADIUS
		)
	]
	const spanOf = (items: Placed[]) => {
		const times = items.map((d) => d.sale.time)
		return { from: Math.min(...times), to: Math.max(...times) }
	}
	const zoomTo = (items: Placed[]) => {
		const { from, to } = spanOf(items)
		// At least a screen of bars around the span: a two-bar window on a
		// four-hour chart is one the library will not show.
		const pad = Math.max(bucketSecs * 12, (to - from) * 0.5)
		onHover(null)
		onZoom(from - pad, to + pad)
	}
	const clusterCard = (c: { x: number; y: number; items: Placed[] }): HoverCard => {
		const items = [...c.items].sort((a, b) => b.sale.time - a.sale.time)
		const prices = items.map((d) => d.sale.price).sort((a, b) => a - b)
		const median = prices[Math.floor(prices.length / 2)]
		const medianSats = items
			.map((d) => d.sale.price_sats)
			.filter((v): v is number => v !== undefined)
			.sort((a, b) => a - b)
		const mid = medianSats.length > 0 ? medianSats[Math.floor(medianSats.length / 2)] : undefined
		const lo = items.reduce((m, d) => Math.min(m, d.sale.price_sats ?? Infinity), Infinity)
		const hi = items.reduce((m, d) => Math.max(m, d.sale.price_sats ?? -Infinity), -Infinity)
		const rangeKnown = Number.isFinite(lo) && Number.isFinite(hi)
		const volume = fungible
			? items.reduce((s, d) => s + (d.sale.price_sats ?? 0) * (d.sale.amount ?? 0), 0)
			: items.reduce((s, d) => s + (d.sale.price_sats ?? 0), 0)
		const { from, to } = spanOf(items)
		const fenced = items[0].fenced
		return {
			x: c.x,
			y: c.y,
			body: (
				<div className="min-w-0 space-y-0.5">
					<div className="flex items-baseline gap-2">
						<span className="text-sm font-semibold tabular-nums text-foreground">
							{items.length} {fenced ? 'sales not in the candles' : 'sales'}
						</span>
						{mid !== undefined && (
							<span className="tabular-nums text-muted-foreground">
								median {formatAxisLabel(mid, 'sats', unit)}
							</span>
						)}
					</div>
					{rangeKnown && (
						<div className="tabular-nums text-muted-foreground">
							Range {formatAxisLabel(lo, 'sats', unit)} – {formatAxisLabel(hi, 'sats', unit)}
						</div>
					)}
					{volume > 0 && (
						<div className="tabular-nums text-muted-foreground">
							{formatAxisLabel(volume, 'sats', unitForSats(volume))} total
						</div>
					)}
					<DeltaLine delta={deltaFor(items[0].sale, median)} label="median" />
					{impliedLine(median, mid)}
					<div className="text-muted-foreground">
						{formatWhen(from)}
						{to !== from && ` – ${formatWhen(to)}`} UTC
					</div>
					{!fungible && (
						<div className="pt-1">
							{items.slice(0, 3).map((d) => (
								<div key={d.key} className="flex items-center gap-2 py-0.5">
									{d.sale.inscription && (
										// eslint-disable-next-line @next/next/no-img-element
										<img
											src={`${CDN_PREVIEW_URL}/${d.sale.inscription}`}
											alt=""
											loading="lazy"
											onError={(e) => {
												e.currentTarget.style.display = 'none'
											}}
											className="h-5 w-5 shrink-0 rounded bg-muted object-cover"
										/>
									)}
									<span className="tabular-nums text-foreground">
										{d.sale.price_sats !== undefined
											? formatAxisLabel(d.sale.price_sats, 'sats', unit)
											: formatAxisLabel(d.sale.price, denom, unit)}
									</span>
									<span className="text-muted-foreground">{formatWhen(d.sale.time)}</span>
								</div>
							))}
							{items.length > 3 && <div className="text-muted-foreground">+{items.length - 3} more</div>}
						</div>
					)}
					<button
						type="button"
						onClick={() => zoomTo(items)}
						className="pt-0.5 font-medium text-[var(--primary-700)] hover:underline dark:text-[var(--primary-500)]"
					>
						Zoom in to these sales
					</button>
				</div>
			)
		}
	}

	// Off-range sales, one chevron per 24px column.
	const pinnedColumns = new Map<
		string,
		{ x: number; y: number; side: 'above' | 'below'; items: Placed[] }
	>()
	for (const d of placed) {
		if (!d.pinned) {
			continue
		}
		const column = Math.round(d.x / 24) * 24
		const key = `${d.pinned}-${column}`
		const group = pinnedColumns.get(key)
		if (group) {
			group.items.push(d)
		} else {
			pinnedColumns.set(key, { x: column, y: d.y, side: d.pinned, items: [d] })
		}
	}


	return (
		<div className="pointer-events-none absolute inset-0 z-10" role="group" aria-label="Sales">
			{clusters.map((c) => {
				const d = c.items[0]
				if (c.items.length === 1) {
					const priceLabel =
						d.sale.price_sats !== undefined
							? formatAxisLabel(d.sale.price_sats, 'sats', unit)
							: formatAxisLabel(d.sale.price, denom, unit)
					const opens = !fungible && Boolean(d.sale.inscription)
					return (
						<button
							key={d.key}
							type="button"
							aria-label={`${d.fenced ? 'Sale not in the candles' : 'Sale'} at ${priceLabel}`}
							aria-describedby={HOVER_CARD_ID}
							onMouseEnter={() => onHover(cardFor(d))}
							onMouseLeave={() => onHover(null)}
							onFocus={() => onHover(cardFor(d))}
							onBlur={() => onHover(null)}
							onClick={() => {
								if (coarsePointer() || !opens) {
									onHover(cardFor(d))
								} else {
									onOpen(d.sale.inscription as string)
								}
							}}
							style={{ left: d.x, top: d.y }}
							className={classNames(
								// A 20px hit area around a 6px mark.
								'pointer-events-auto absolute flex size-5 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary-500)]',
								opens ? 'cursor-pointer' : 'cursor-default'
							)}
						>
							<span
								className={classNames(
									'block size-1.5 rounded-full',
									d.fenced
										? 'border border-neutral-300/80 bg-transparent'
										: 'bg-neutral-200/70 ring-1 ring-white/20'
								)}
							/>
						</button>
					)
				}
				const r = clusterRadius(c.items.length)
				const size = Math.round(r * 2)
				const labelled = r >= 7.5
				return (
					<button
						key={`c-${d.key}-${c.items.length}`}
						type="button"
						aria-label={`${c.items.length} ${d.fenced ? 'sales not in the candles' : 'sales'}; zoom in`}
						aria-describedby={HOVER_CARD_ID}
						onMouseEnter={() => onHover(clusterCard(c))}
						onMouseLeave={() => onHover(null)}
						onFocus={() => onHover(clusterCard(c))}
						onBlur={() => onHover(null)}
						onClick={() => (coarsePointer() ? onHover(clusterCard(c)) : zoomTo(c.items))}
						style={{ left: c.x, top: c.y, width: Math.max(size, 20), height: Math.max(size, 20) }}
						className="pointer-events-auto absolute flex -translate-x-1/2 -translate-y-1/2 cursor-zoom-in items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary-500)]"
					>
						<span
							style={{ width: size, height: size }}
							className={classNames(
								'flex items-center justify-center rounded-full text-[9px] font-semibold leading-none tabular-nums',
								d.fenced
									? 'border border-neutral-300/80 bg-neutral-900/40 text-neutral-300'
									: 'bg-neutral-200/80 text-neutral-900 ring-1 ring-white/25'
							)}
						>
							{labelled ? c.items.length : ''}
						</span>
					</button>
				)
			})}
			{[...pinnedColumns.values()].map((g) => (
				<button
					key={`${g.side}-${g.x}`}
					type="button"
					aria-label={`${g.items.length} ${g.items.length === 1 ? 'sale' : 'sales'} ${
						g.side
					} the view; zoom in`}
					onMouseEnter={() => onHover(g.items.length === 1 ? cardFor(g.items[0]) : clusterCard({ x: g.x, y: g.y, items: g.items }))}
					onMouseLeave={() => onHover(null)}
					onFocus={() => onHover(g.items.length === 1 ? cardFor(g.items[0]) : clusterCard({ x: g.x, y: g.y, items: g.items }))}
					onBlur={() => onHover(null)}
					onClick={() => zoomTo(g.items)}
					style={{ left: g.x, top: g.y }}
					className="pointer-events-auto absolute flex min-h-5 min-w-6 -translate-x-1/2 -translate-y-1/2 cursor-zoom-in items-center justify-center gap-0.5 rounded px-1 text-[10px] leading-none text-neutral-400 tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary-500)]"
				>
					<svg width="8" height="5" viewBox="0 0 8 5" aria-hidden="true" className="fill-current">
						{g.side === 'above' ? <path d="M0 5 L4 0 L8 5 Z" /> : <path d="M0 0 L4 5 L8 0 Z" />}
					</svg>
					{g.items.length > 1 && <span>{g.items.length}</span>}
				</button>
			))}
		</div>
	)
}

/**
 * Thesis authors' faces, hung on the plot at the time they posted.
 *
 * A thesis is a person, and a face is how you recognise one. Posts snap to
 * the bar they belong to and group by pixel column; several on one candle
 * stack upward from it, one face per person, with a count where someone
 * posted more than once. Each face is its own button and opens that
 * person's profile; the card lists the posts, each with its author as the
 * link, so a stack of six theses by three people is never one button that
 * opens whoever happened to be first.
 */
function ThesisFaces({
	geometry,
	theses,
	bars,
	onHover
}: {
	geometry: ChartGeometry
	theses: ThesisMarker[]
	/** The series, seconds and close, so a thesis sits on its bar. */
	bars: { time: number; close: number }[]
	onHover: (card: HoverCard | null) => void
}) {
	if (theses.length === 0) {
		return null
	}
	const snapToBar = (secs: number): { time: number; close: number } | null => {
		let best: { time: number; close: number } | null = null
		let bestGap = Infinity
		for (const b of bars) {
			const gap = Math.abs(b.time - secs)
			if (gap < bestGap) {
				bestGap = gap
				best = b
			}
		}
		return best
	}
	const clusters = new Map<number, { y: number; posts: ThesisMarker[] }>()
	for (const thesis of theses) {
		const ms = thesis.post.createdAtMs
		if (!ms) {
			continue
		}
		const bar = snapToBar(Math.floor(ms / 1000))
		if (!bar) {
			continue
		}
		const x = geometry.timeToX(bar.time)
		if (x === null) {
			continue
		}
		const y = geometry.priceToY(bar.close)
		const top = geometry.plot.top + 4
		const bottom = geometry.plot.top + geometry.plot.height - 4
		const anchor = y === null ? top : Math.min(Math.max(y, top), bottom)
		// 32px columns: a 28px face never overlaps its neighbour.
		const column = Math.round(x / 32) * 32
		const group = clusters.get(column)
		if (group) {
			group.posts.push(thesis)
			group.y = Math.min(group.y, anchor)
		} else {
			clusters.set(column, { y: anchor, posts: [thesis] })
		}
	}

	const openProfile = (userId: number | undefined) => {
		if (Number.isFinite(userId)) {
			void openTwetchUserProfile(userId as number)
		}
	}
	const Face = ({ user, className }: { user?: TwetchUser; className?: string }) => {
		const src = resolveTwetchImage(user?.icon)
		return src ? (
			// eslint-disable-next-line @next/next/no-img-element
			<img
				src={src}
				alt=""
				loading="lazy"
				width={28}
				height={28}
				className={classNames('rounded-full object-cover', pixelArtClass(user?.icon) ?? '', className)}
			/>
		) : (
			<span
				className={classNames(
					'flex items-center justify-center rounded-full bg-[var(--primary-500)] font-semibold text-white',
					className
				)}
			>
				{(user?.name ?? 'T').slice(0, 1).toUpperCase()}
			</span>
		)
	}

	return (
		<div className="pointer-events-none absolute inset-0 z-[12]" aria-label="Theses">
			{[...clusters.entries()].map(([x, { y, posts }]) => {
				// One face per person, newest first, with how many times they posted.
				const people = new Map<number, { user?: TwetchUser; count: number; latest: number }>()
				for (const { post, user } of posts) {
					const p = people.get(post.userId)
					if (p) {
						p.count += 1
						p.latest = Math.max(p.latest, post.createdAtMs ?? 0)
					} else {
						people.set(post.userId, { user, count: 1, latest: post.createdAtMs ?? 0 })
					}
				}
				const stack = [...people.entries()].sort((a, b) => b[1].latest - a[1].latest)
				const ordered = [...posts].sort((a, b) => (b.post.createdAtMs ?? 0) - (a.post.createdAtMs ?? 0))
				const card = (): HoverCard => ({
					x,
					y: y - 34,
					body: (
						<div className="flex flex-col gap-2">
							{ordered.slice(0, 4).map(({ post, user }) => (
								<div key={post.id} className="flex gap-2">
									<Face user={user} className="mt-0.5 h-5 w-5 shrink-0 text-[9px]" />
									<div className="min-w-0">
										<div className="flex items-baseline gap-2">
											<button
												type="button"
												onClick={() => openProfile(post.userId)}
												className="truncate font-semibold text-foreground hover:text-[var(--primary-700)] hover:underline dark:text-[var(--primary-500)]"
											>
												{user?.name ?? 'Thesis'}
											</button>
											{post.createdAtMs ? (
												<span className="shrink-0 text-muted-foreground">
													{formatWhen(Math.floor(post.createdAtMs / 1000))} UTC
												</span>
											) : null}
										</div>
										<div className="line-clamp-3 whitespace-pre-wrap break-words text-foreground/90">
											{(post.content ?? '').trim().slice(0, THESIS_TEXT_LIMIT) || 'Thesis'}
										</div>
									</div>
								</div>
							))}
							{ordered.length > 4 && (
								<div className="text-muted-foreground">+{ordered.length - 4} more in the Social tab</div>
							)}
						</div>
					)
				})
				const label = `${posts.length} ${posts.length === 1 ? 'thesis' : 'theses'} by ${stack.length} ${
					stack.length === 1 ? 'person' : 'people'
				}`
				return (
					<div
						key={x}
						role="group"
						data-testid="chart-thesis-marker"
						aria-label={label}
						onMouseEnter={() => onHover(card())}
						onMouseLeave={() => onHover(null)}
						style={{ left: x, top: y - 24 }}
						// Several theses on one candle stack upward from it, one
						// face per person, so the column reads as "this bar"
						// rather than smearing sideways across its neighbours.
						className="pointer-events-auto absolute flex -translate-x-1/2 -translate-y-full flex-col-reverse items-center"
					>
						{stack.slice(0, 3).map(([userId, p], i) => (
							<button
								key={userId}
								type="button"
								aria-label={`${p.user?.name ?? 'Thesis'}${p.count > 1 ? `, ${p.count} theses` : ''}: open profile`}
								aria-describedby={HOVER_CARD_ID}
								onFocus={() => onHover(card())}
								onBlur={() => onHover(null)}
								onClick={() => (coarsePointer() ? onHover(card()) : openProfile(userId))}
								className={classNames(
									'relative rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary-500)]',
									i > 0 ? '-mb-2' : ''
								)}
							>
								<Face
									user={p.user}
									className="h-7 w-7 border-2 border-[var(--primary-500)]/80 text-[11px] shadow-md"
								/>
								{p.count > 1 && (
									<span className="absolute -right-1 -top-1 rounded-full bg-[var(--primary-500)] px-1 text-[9px] font-semibold leading-3 text-white">
										{p.count}
									</span>
								)}
							</button>
						))}
						{stack.length > 3 && (
							<span className="-mb-1 rounded-full bg-[var(--primary-500)] px-1.5 text-[10px] font-semibold text-white">
								+{stack.length - 3}
							</span>
						)}
					</div>
				)
			})}
		</div>
	)
}

export default function TvChart({
	kind,
	id,
	title,
	denom: initialDenom = 'sats',
	denoms = ['sats', 'usd'],
	denomLabels,
	resolution = DEFAULT_RESOLUTION,
	getMarks,
	compact = false,
	onHoverPrice,
	supplyHint,
	resolveSupplyHint,
	typicalPrice,
	theses,
	onRangeChange,
	executions,
	bars,
	onDenomChange,
	onResolutionChange,
	overlay,
	className
}: TvChartProps) {
	const container = useRef<HTMLDivElement>(null)
	// `useId` rather than a counter: two charts for the same asset on one page
	// must not collide, and the id has to be stable across re-renders or the
	// widget is torn down and rebuilt on every parent update.
	const domId = `tv-${useId().replace(/[^a-zA-Z0-9]/g, '')}`
	const widgetRef = useRef<TvWidget | null>(null)
	const { resolvedTheme } = useTheme()
	const router = useRouter()
	const btcUsd = useBtcUsd()

	// Recalled lazily so the first paint already uses the remembered
	// denomination rather than flashing the default and switching.
	const [denom, setDenom] = useState<Denom>(() => {
		if (typeof window === 'undefined') {
			return initialDenom
		}
		const saved = recallPreference('denom') as Denom | null
		// Only honour a remembered value this chart actually offers: a market
		// cap preference must not strand a viewer on a market page that has no
		// such series.
		return saved && denoms.includes(saved) ? saved : initialDenom
	})
	const [degraded, setDegraded] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [geometry, setGeometry] = useState<ChartGeometry | null>(null)
	const [outliers, setOutliers] = useState<ChartOutlier[]>([])
	const [prints, setPrints] = useState<ChartOutlier[]>([])
	const [hoverCard, setHoverCard] = useState<HoverCard | null>(null)
	// The card closes a beat after the pointer leaves its anchor, not at once,
	// so the pointer can travel from a dot into the card and use its links.
	const hoverCloseRef = useRef<number | null>(null)
	const cancelHoverClose = () => {
		if (hoverCloseRef.current !== null) {
			window.clearTimeout(hoverCloseRef.current)
			hoverCloseRef.current = null
		}
	}
	// Escape dismisses the card, as it does every other transient surface.
	useEffect(() => {
		if (!hoverCard) {
			return
		}
		const close = () => {
			cancelHoverClose()
			setHoverCard(null)
		}
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				close()
			}
		}
		// A tap anywhere but the card or a marker dismisses it: on a touch
		// screen there is no pointer to leave with.
		const onPointerDown = (e: PointerEvent) => {
			const t = e.target as Element | null
			if (t?.closest(`#${HOVER_CARD_ID}, [aria-describedby="${HOVER_CARD_ID}"]`)) {
				return
			}
			close()
		}
		window.addEventListener('keydown', onKey)
		document.addEventListener('pointerdown', onPointerDown, true)
		return () => {
			window.removeEventListener('keydown', onKey)
			document.removeEventListener('pointerdown', onPointerDown, true)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hoverCard])
	useEffect(() => () => cancelHoverClose(), [])
	const hover = (card: HoverCard | null) => {
		cancelHoverClose()
		if (card) {
			setHoverCard(card)
			return
		}
		hoverCloseRef.current = window.setTimeout(() => {
			hoverCloseRef.current = null
			setHoverCard(null)
		}, 220)
	}
	// The band's values at the crosshair, for a readout under the legend.
	const [bandAtCursor, setBandAtCursor] = useState<{
		fair: number
		p10: number
		p90: number
	} | null>(null)
	// History arrives in pages, newest first, and each page carries only its
	// own window's sales. The pages are MERGED here: replacing state with
	// each page left the chart holding whichever page came last -- the
	// oldest, usually off-screen -- and drawing nothing.
	const outliersByKeyRef = useRef<Map<string, ChartOutlier>>(new Map())
	const printsByKeyRef = useRef<Map<string, ChartOutlier>>(new Map())
	const mergeSales = (into: Map<string, ChartOutlier>, found: ChartOutlier[]) => {
		for (const o of found) {
			into.set(`${o.time}-${o.price}`, o)
		}
		return [...into.values()].sort((a, b) => a.time - b.time)
	}
	const hasPrintsRef = useRef(false)
	const thesesRef = useRef(theses)
	const hasThesesRef = useRef(false)
	const [showTheses, setShowTheses] = useState(
		() => typeof window === 'undefined' || recallPreference('theses') !== '0'
	)
	// Read inside the widget's subscription, which is created once and must not
	// close over a stale value.
	const hasOutliersRef = useRef(false)
	const hasTrendRef = useRef(false)
	// Overlay switches, remembered like the denomination. On by default.
	const [showBand, setShowBand] = useState(
		() => typeof window === 'undefined' || recallPreference('band') !== '0'
	)
	// Sales start off: two hundred dots over a month of candles is a cloud,
	// and the candles already say where the money went. A visitor who turns
	// them on keeps them on.
	const [showPrints, setShowPrints] = useState(
		() => typeof window !== 'undefined' && recallPreference('prints') === '1'
	)
	const kindRef = useRef(kind)
	// The server's own verdict on the series it served. Shown on the chart,
	// not behind a debug flag: a broken series is something to see.
	const [violations, setViolations] = useState<ChartViolation[]>([])
	// Bumped when a band arrives, so the overlay redraws. The store itself is
	// module-level -- the datafeed writes to it without a React path -- so
	// something has to tell React that it moved.
	const [bandRev, setBandRev] = useState(0)
	// Every symbol this chart has shown, so its bands can be released on
	// unmount. A chart that lived through three denomination switches wrote
	// under three keys.
	const symbolsSeenRef = useRef<Set<string>>(new Set())

	// Stable per mounted component, for the lifecycle log.
	const chartIdRef = useRef<string>('')
	if (!chartIdRef.current) {
		chartIdRef.current = nextChartId()
	}
	const chartId = chartIdRef.current

	const symbol = buildSymbol(kind, id, denom)
	const hasBars = Boolean(bars)

	// Marks are passed through a ref so that changing the callback identity —
	// which happens on every render of a parent holding social state — does not
	// tear down and rebuild the chart.
	const marksRef = useRef(getMarks)
	useEffect(() => {
		marksRef.current = getMarks
	}, [getMarks])

	// Callbacks are held in refs for the same reason: a parent that re-renders
	// on every hover would otherwise rebuild the widget on every hover.
	const hoverRef = useRef(onHoverPrice)
	const supplyHintRef = useRef(supplyHint)
	const resolveSupplyRef = useRef(resolveSupplyHint)
	const typicalPriceRef = useRef(typicalPrice)
	const rangeRef = useRef(onRangeChange)
	const execRef = useRef(executions)
	// Read through a ref so new bars do not rebuild the widget; the effect
	// below resets the chart's data instead.
	const staticBarsRef = useRef(bars)
	const denomChangeRef = useRef(onDenomChange)
	const resolutionChangeRef = useRef(onResolutionChange)
	const overlayRef = useRef(overlay)
	/// The resolution the chart is ACTUALLY on.
	///
	/// `resolution` is the prop, which is whatever the page opened with. Once a
	/// viewer picks a different candle width in the toolbar the two diverge,
	/// and handing the prop to `setSymbol` on a denomination change snapped
	/// them back to the page default — switch from Mcap to USD on a daily
	/// chart and it silently became hourly.
	// Config the widget reads once at construction. Held in refs so that a new
	// array or object literal from a parent render — `denoms={['sats','usd']}`
	// is a fresh array every time — cannot become a reason to rebuild.
	const symbolRef = useRef(symbol)
	// The remembered candle width wins over the caller's opening one, so a
	// viewer who chose 1D keeps 1D from one collection to the next. Only a
	// width the toolbar offers; it cannot show one it does not.
	const resolutionRef = useRef(
		(() => {
			if (typeof window === 'undefined') {
				return resolution
			}
			const saved = recallPreference('resolution')
			return saved && (SUPPORTED_RESOLUTIONS as readonly string[]).includes(saved)
				? saved
				: resolution
		})()
	)
	// The resolution the widget is actually showing, kept current by the
	// interval subscription. Starts as what the widget is built with.
	const liveResolutionRef = useRef(resolutionRef.current)
	const denomsRef = useRef(denoms)
	// The header switcher's own setter, so a denomination the CHART chose --
	// the market-cap fallback -- is reflected in the control, not only in the
	// axis. Null until the header exists.
	const switcherSetRef = useRef<((value: Denom) => void) | null>(null)
	const denomLabelsRef = useRef(denomLabels)
	const denomRef = useRef(denom)
	useEffect(() => {
		hoverRef.current = onHoverPrice
		supplyHintRef.current = supplyHint
		resolveSupplyRef.current = resolveSupplyHint
		typicalPriceRef.current = typicalPrice
		rangeRef.current = onRangeChange
	}, [onHoverPrice, onRangeChange, supplyHint, resolveSupplyHint, typicalPrice])

	/// Set by the widget effect once `drawExecutions` exists in its closure.
	const redrawExecutionsRef = useRef<(() => void) | null>(null)
	/// Re-publish geometry using the range the chart is already showing.
	const publishGeometryRef = useRef<(() => void) | null>(null)
	// The per-frame plot tracker, so unmount can stop it.
	const trackerRef = useRef<number | null>(null)
	// The newest bar's time in seconds, the anchor for bar-index geometry.
	const lastBarTimeRef = useRef(0)
	// True while the plot is changing size. The library re-lays its panes
	// over a few frames on a resize and an overlay drawn against a plot that
	// is half-way there is worse than no overlay; it is hidden until the
	// size has held still.
	const [settling, setSettling] = useState(false)
	const settlingRef = useRef(false)
	const settleTimerRef = useRef<number | null>(null)

	useEffect(() => {
		staticBarsRef.current = bars
	}, [bars])

	useEffect(() => {
		denomChangeRef.current = onDenomChange
		resolutionChangeRef.current = onResolutionChange
		overlayRef.current = overlay
		denomsRef.current = denoms
		denomLabelsRef.current = denomLabels
		denomRef.current = denom
		switcherSetRef.current?.(denom)
		thesesRef.current = theses
		hasThesesRef.current = (theses?.length ?? 0) > 0
		kindRef.current = kind
		symbolRef.current = symbol
	}, [
		onDenomChange,
		onResolutionChange,
		overlay,
		denoms,
		denomLabels,
		denom,
		symbol,
		showBand,
		showPrints,
		showTheses,
		theses,
		kind
	])

	// Fills can arrive after the chart is built — a swap the viewer just made
	// should appear without rebuilding the widget under them.
	useEffect(() => {
		execRef.current = executions
		redrawExecutionsRef.current?.()
	}, [executions])

	// Bars the datafeed has delivered, so a range change can be answered from
	// data we already hold rather than asking the widget to export itself.
	const barsRef = useRef<Bar[]>([])
	// Set once per widget, so a user who has scrolled is never yanked back.
	const didFitRef = useRef(false)
	// Execution arrows currently on the plot, so a change replaces them rather
	// than stacking a second set on top of the first.
	const execShapesRef = useRef<TvExecutionShape[]>([])
	const chartReadyRef = useRef(false)

	const stableGetMarks = useCallback(
		(s: string, from: number, to: number) =>
			marksRef.current ? marksRef.current(s, from, to) : Promise.resolve([]),
		[]
	)

	useEffect(() => {
		let cancelled = false
		// Captured once: the Set identity never changes, and reading the ref in
		// the cleanup would be reading it after the component has gone.
		const symbolsSeen = symbolsSeenRef.current
		const dark = resolvedTheme !== 'light'
		chartLog(chartId, 'mount: building widget', {
			symbol: symbolRef.current,
			resolution: resolutionRef.current,
			theme: dark ? 'Dark' : 'Light'
		})

		loadTradingView()
			.then(() => {
				if (cancelled || !container.current) {
					return
				}
				const TV = (
					window as unknown as { TradingView: { widget: new (config: unknown) => TvWidget } }
				).TradingView
				if (!TV?.widget) {
					setError('chart bundle unavailable')
					return
				}

				// A collection whose last trade was weeks ago leaves TradingView
				// parked on empty space to the right of its own history, which
				// reads as a broken chart. Land the viewport on the data that
				// actually exists — once, so a user who has scrolled away is
				// never yanked back.
				const fitToData = () => {
					if (didFitRef.current || cancelled) {
						return
					}
					const bars = barsRef.current
					const chart = widgetRef.current?.activeChart?.()
					if (!chart?.setVisibleRange || bars.length < 2) {
						return
					}
					didFitRef.current = true
					const tail = bars.slice(-VISIBLE_BARS_ON_LOAD)
					void Promise.resolve(
						chart.setVisibleRange({
							from: Math.floor(tail[0].time / 1000),
							to: Math.floor(tail[tail.length - 1].time / 1000)
						})
					).catch(() => {
						// Range setting is best-effort; the chart is still usable
						// at whatever window the library chose.
					})
				}

				/// Draw the viewer's own fills as buy/sell arrows.
				///
				/// `createExecutionShape` is TradingView's own primitive for
				/// this and is why the port is worth doing: the old markers
				/// were snapped to the nearest bar and vanished at resolutions
				/// where that bar did not exist. An execution carries its own
				/// timestamp, so it stays put across every resolution and zoom.
				///
				/// A fill with no recorded price is pinned to its bar's close
				/// rather than dropped or guessed: the time is known and the
				/// price is not, and the close is the one price on that bar we
				/// can state without inventing anything.
				const drawExecutions = () => {
					if (!chartReadyRef.current || cancelled) {
						return
					}
					const chart = widgetRef.current?.activeChart?.()
					if (!chart?.createExecutionShape) {
						return
					}

					for (const shape of execShapesRef.current) {
						try {
							shape.remove?.()
						} catch {
							// Already gone with a previous symbol's chart.
						}
					}
					execShapesRef.current = []

					const fills = execRef.current ?? []
					if (fills.length === 0) {
						return
					}
					const bars = barsRef.current
					const up = tokenColor('--success-ink', '#12B76A', container.current)
					const down = tokenColor('--destructive-ink', '#ef4444', container.current)

					for (const fill of fills) {
						const price =
							fill.price ??
							bars.reduce<number | null>((best, b) => {
								return b.time <= fill.ts * 1000 ? b.close : best
							}, null)
						if (price == null || !Number.isFinite(price)) {
							continue
						}
						try {
							const shape = chart.createExecutionShape!()
							shape
								.setText?.(fill.side === 'buy' ? 'B' : 'S')
								?.setTooltip?.(fill.tooltip ?? (fill.side === 'buy' ? 'You bought' : 'You sold'))
								?.setTextColor?.(fill.side === 'buy' ? up : down)
								?.setArrowColor?.(fill.side === 'buy' ? up : down)
								?.setDirection?.(fill.side)
								?.setTime?.(fill.ts)
								?.setPrice?.(price)
							execShapesRef.current.push(shape)
						} catch {
							// One bad fill must not cost the whole overlay.
						}
					}
				}

				/// Measure the plot and publish a time-to-pixel mapping.
				///
				/// The mapping is proportional across the visible range. That is
				/// exact here because these markets are 24x7: there are no
				/// session gaps for the time scale to compress, so time and
				/// pixels are linear in each other.
				const publishGeometry = (from: number, to: number) => {
					if (cancelled || !container.current || to <= from) {
						return
					}
					const frame = container.current.querySelector('iframe')
					const doc = frame?.contentDocument
					// The price axis is a sibling of the plot; measuring the
					// drawing canvas itself is what keeps the overlay aligned
					// when the axis width changes with the number of digits.
					const canvas = doc?.querySelector('canvas')
					if (!frame || !canvas) {
						chartLog(chartId, 'geometry: no plot canvas yet', { frame: !!frame })
						return
					}
					const frameRect = frame.getBoundingClientRect()
					const hostRect = container.current.getBoundingClientRect()
					const canvasRect = canvas.getBoundingClientRect()

					const plot = {
						left: frameRect.left - hostRect.left + canvasRect.left,
						top: frameRect.top - hostRect.top + canvasRect.top,
						width: canvasRect.width,
						height: canvasRect.height
					}

					// The library's OWN mappings, not a re-derivation of them.
					// This is what makes an overlay legitimate: the band is
					// positioned by the same functions the candles are, so it
					// follows pan, zoom, autoscale and log scale exactly.
					//
					// Time: the API exposes coordinate->time but not the
					// inverse, so the inverse is taken from two probes. Bars
					// here are gapless (the feed fills empty buckets), so time
					// is linear in x and two points define the line -- and,
					// unlike the visible range, the probes carry the right
					// offset and the half-bar alignment.
					const active = widgetRef.current?.activeChart?.()
					const scale = active?.getPanes?.()?.[0]?.getMainSourcePriceScale?.()
					// Time, by bar index. `coordinateToTime` is a step function
					// (the bar under a pixel), so a line fitted through two
					// probes of it is off by up to a bar, differently after
					// every zoom and resize -- which is the band drifting off
					// the candles. The library exposes what it actually draws
					// with: pixels per bar, and the whitespace to the right of
					// the last bar. The last bar's centre is width minus that
					// whitespace minus half a bar, and every other bar is a
					// whole number of bar widths to its left.
					let barLine: { xLast: number; secs: number; spacing: number } | null = null
					try {
						const timeScale = active?.getTimeScale?.()
						const last = barsRef.current[barsRef.current.length - 1]
						const secs = resolutionSeconds(liveResolutionRef.current || resolution)
						if (timeScale && last && secs > 0) {
							const spacing = timeScale.barSpacing()
							const xLast = timeScale.width() - timeScale.rightOffset() * spacing - spacing / 2
							barLine = { xLast, secs, spacing }
							// Anchor: the newest bar's time, in seconds.
							lastBarTimeRef.current = Math.floor(last.time / 1000)
						}
					} catch {
						// Fall through to the visible-range approximation.
					}

					chartLog(chartId, 'geometry published', { from, to, plot, scale: !!scale })
					setGeometry({
						from,
						to,
						plot,
						timeToX: (ts: number) => {
							const x = barLine
								? barLine.xLast - ((lastBarTimeRef.current - ts) / barLine.secs) * barLine.spacing
								: ((ts - from) / (to - from)) * plot.width
							if (!Number.isFinite(x) || x < 0 || x > plot.width) {
								return null
							}
							return plot.left + x
						},
						priceToY: (price: number) => {
							if (!scale || !Number.isFinite(price)) {
								return null
							}
							try {
								const y = scale.priceToCoordinate(price)
								return y == null || !Number.isFinite(y) ? null : plot.top + y
							} catch {
								// The scale throws while the pane is being
								// rebuilt; the next publish will have it.
								return null
							}
						}
					})
				}

				const paintChrome = () => {
					if (cancelled) {
						return
					}
					const surface = surfaceColor(container.current, dark ? '#0a0a0a' : '#ffffff')
					const frame = container.current?.querySelector('iframe')
					const doc = frame?.contentDocument
					if (!doc?.head) {
						return
					}
					const ID = 'ow-surface-paint'
					doc.getElementById(ID)?.remove()
					const style = doc.createElement('style')
					style.id = ID
					// Only surfaces — never the drawing area's own canvas, which
					// the library paints from `paneProperties`.
					style.textContent = `
						html, body { background: ${surface} !important; }
						.layout__area--top,
						.layout__area--left,
						.layout__area--right,
						.layout__area--bottom,
						.chart-container,
						.chart-gui-wrapper { background: ${surface} !important; }
					`
					doc.head.appendChild(style)
				}

				// What the widget is actually showing. A symbol change that
				// arrives before the chart is ready cannot be applied yet, so
				// it is reconciled against this once it is.
				const constructedSymbol = symbolRef.current

				const widget = new TV.widget({
					container: container.current,
					library_path: '/charting_library/',
					symbol: symbolRef.current,
					interval: resolutionRef.current,
					datafeed: createDatafeed({
						onDegraded: () => !cancelled && setDegraded(true),
						supplyHint: () => supplyHintRef.current,
						resolveSupplyHint: () =>
							resolveSupplyRef.current?.() ?? Promise.resolve(undefined),
						typicalPrice: (d) => typicalPriceRef.current?.(d),
						onInvariants: (_sym, found) => {
							if (!cancelled) {
								setViolations(found)
							}
						},
						onPrints: (_sym, found) => {
							if (!cancelled) {
								const merged = mergeSales(printsByKeyRef.current, found)
								hasPrintsRef.current = merged.length > 0
								setPrints(merged)
								publishGeometryRef.current?.()
							}
						},
						onOutliers: (_sym, found) => {
							if (!cancelled) {
								const merged = mergeSales(outliersByKeyRef.current, found)
								hasOutliersRef.current = merged.length > 0
								setOutliers(merged)
								// The first batch usually lands after the range
								// subscription has already fired, so publish
								// now rather than waiting for the next pan.
								publishGeometryRef.current?.()
							}
						},
						onDenomUnavailable: (_sym, reason) => {
							if (cancelled) {
								return
							}
							chartLog(chartId, 'denomination unavailable', { reason })
							// Market cap needs a circulating supply, which some
							// assets simply do not have here. Rather than leave
							// a remembered preference stranding the viewer on an
							// empty chart, drop to the next denomination the
							// chart offers and say nothing — the axis label
							// already states what is being shown.
							setDenom((current) => {
								if (current === 'sats') {
									return current
								}
								const next = denomsRef.current.find((d) => d !== current)
								return next ?? 'sats'
							})
						},
						onTrend: (sym, points) => {
							if (cancelled) {
								return
							}
							symbolsSeen.add(sym)
							putTrend(sym, points)
							hasTrendRef.current = points.length > 1
							setBandRev(trendRevision())
							chartLog(chartId, 'band received', { symbol: sym, points: points.length })
							// The overlay needs a geometry to draw against, and
							// the band usually lands after the range
							// subscription has already fired. Same reason the
							// outlier handler publishes here.
							publishGeometryRef.current?.()
						},
						getMarks: stableGetMarks,
						staticBars: bars ? () => staticBarsRef.current ?? [] : undefined,
						displayName: title,
						onBars: (_s, bars) => {
							// Merge and keep sorted: the datafeed pages backwards,
							// so later batches are older than earlier ones.
							const merged = new Map(barsRef.current.map((b) => [b.time, b]))
							for (const b of bars) {
								merged.set(b.time, b)
							}
							barsRef.current = [...merged.values()].sort((a, b) => a.time - b.time)
							fitToData()
							drawExecutions()
						},
						onTick: (_s, bar) => {
							const merged = new Map(barsRef.current.map((b) => [b.time, b]))
							merged.set(bar.time, bar)
							barsRef.current = [...merged.values()].sort((a, b) => a.time - b.time)
						}
					}),
					locale: 'en',
					autosize: true,
					theme: dark ? 'Dark' : 'Light',
					timezone: 'Etc/UTC',
					// The full terminal: resolution tabs, chart-type picker,
					// indicators, drawing tools down the left, the range tabs
					// along the bottom, fullscreen and undo/redo. Only symbol
					// search and compare are closed off, because the site's own
					// search knows about collections and the chart's does not.
					//
					// Deep history is paged in by the datafeed as the user
					// scrolls back, which is the behaviour the old charts
					// lacked: they loaded a fixed range and simply ended.
					disabled_features: [
						// The library's onboarding toasts ("Press and hold to see
						// detailed chart values", "hold ⌘ while zooming…") show on
						// every visit and cover the plot.
						'popup_hints',
						'header_symbol_search',
						'symbol_search_hot_key',
						'header_compare',
						'compare_symbol',
						'display_market_status',
						// In compact embeds the page supplies its own header and
						// the plot is only a few hundred pixels tall, so the
						// toolbars would take more room than the chart.
						...(compact
							? ['header_widget', 'left_toolbar', 'timeframes_toolbar', 'control_bar']
							: [])
					],
					enabled_features: [
						// The library frames the chart from a blob: URL by default,
						// and Safari refuses a blob frame under the site's
						// frame-ancestors policy -- a white rectangle where the
						// chart should be, in both themes, while Chrome draws it.
						// This loads the frame from a plain URL instead.
						'iframe_loading_compatibility_mode',
						'seconds_resolution',
						// The drawing tools start folded away: the chart opens on
						// the price, and the rail is a click on the toolbar's
						// edge for whoever wants to draw.
						'hide_left_toolbar_by_default',
						'save_chart_properties_to_local_storage',
						'items_favoriting',
						'header_in_fullscreen_mode',
						'use_localstorage_for_settings'
					],
					// Axis, legend and crosshair all read through one formatter, so
					// a market cap prints as `28.53M` rather than `28,530,813`.
					// On a price axis the magnitude is what you read first;
					// counting digits to tell twenty-eight million from two
					// hundred and eighty is work the label should be doing.
					//
					// Keyed off the symbol's own denomination rather than a
					// captured variable, because `setSymbol` swaps the
					// denomination underneath a widget that is never rebuilt —
					// a captured `denom` would format USD as satoshis after the
					// first switch.
					custom_formatters: {
						priceFormatterFactory: (symbolInfo: { name?: string } | null) => {
							const parsed = symbolInfo?.name ? parseSymbol(symbolInfo.name) : null
							if (!parsed) {
								// No opinion — let the library format it.
								return null
							}
							const name = symbolInfo?.name ?? ''
							return {
								// The unit is looked up per call: it is decided by
								// the first page of bars, which arrives after this
								// factory runs.
								// The label carries the unit, so an axis reads
								// "0.01504 BTC" or "7.08K sats" rather than a bare
								// number whose unit is in a switcher elsewhere.
								format: (price: number) => formatAxisLabel(price, parsed.denom, unitFor(name))
							}
						}
					},
					loading_screen: LOADING_SCREEN(dark, container.current),
					custom_css_url: TV_CSS_URL,
					overrides: chartOverrides(dark, container.current)
				})
				widgetRef.current = widget
				redrawExecutionsRef.current = drawExecutions
				if (debugEnabled()) {
					// For probing the geometry against the library's own mappings.
					;(window as unknown as { __owChart?: unknown }).__owChart = widget
				}
				// A caller that owns the resolution (the alkanes chart sizes
				// its fetch by it) must learn that the widget opened on the
				// remembered width rather than the one it passed.
				if (resolutionRef.current !== resolution) {
					resolutionChangeRef.current?.(resolutionRef.current)
				}
				publishGeometryRef.current = () => {
					let range: { from: number; to: number } | undefined
					try {
						range = widget.activeChart?.()?.getVisibleRange?.()
					} catch (e) {
						chartLog(chartId, 'geometry: chart not ready for a range', { error: String(e) })
						return
					}
					if (range) {
						publishGeometry(range.from, range.to)
					} else {
						chartLog(chartId, 'geometry: no visible range yet')
					}
				}
				chartLog(chartId, 'widget constructed')

				// Denomination lives in the chart's own header, beside the
				// resolution tabs — it changes what the price axis MEANS, so it
				// belongs with the other controls that change what is drawn
				// rather than in a strip above the chart that can drift out of
				// sync with it.
				const headerDenoms = denomsRef.current
				if (headerDenoms.length > 1) {
					widget
						.headerReady?.()
						.then(() => {
							if (cancelled || !widget.createButton) {
								return
							}
							const host = widget.createButton({
								align: 'left',
								useTradingViewStyle: false
							})
							switcherSetRef.current = renderSwitcher(
								host,
								headerDenoms.map((d) => ({
									value: d,
									label: denomLabelsRef.current?.[d] ?? DENOM_LABELS[d]
								})),
								denomRef.current,
								(next) => {
									setDenom(next)
									rememberPreference('denom', next)
									denomChangeRef.current?.(next)
								},
								'Price denomination'
							)
						})
						.catch(() => {
							// No header on this layout (compact embeds disable
							// it). The chart is still fully usable; it just has
							// no denomination switcher, which is what compact
							// callers asked for.
						})
				}

				widget.onChartReady?.(() => {
					if (cancelled) {
						return
					}
					const chart = widget.activeChart?.()
					if (!chart) {
						return
					}
					chartReadyRef.current = true
					chartLog(chartId, 'chart ready')

					// Reconcile a symbol that changed during construction.
					//
					// A denomination the asset cannot serve is discovered by
					// the FIRST bar request, which happens while the widget is
					// still building — so the fallback to another denomination
					// lands before the chart is ready, and the effect that
					// normally applies it returns early. Without this the
					// widget stays on the symbol it was born with and shows an
					// empty chart forever, which is exactly what BRC-20
					// collections did: they default to market cap, have no
					// token supply, fall back, and never moved.
					if (symbolRef.current !== constructedSymbol && widget.setSymbol) {
						chartLog(chartId, 'reconciling symbol changed during build', {
							from: constructedSymbol,
							to: symbolRef.current
						})
						barsRef.current = []
						didFitRef.current = false
						try {
							widget.setSymbol(symbolRef.current, liveResolutionRef.current || resolution, () =>
								chartLog(chartId, 'symbol applied', { symbol: symbolRef.current })
							)
						} catch (e) {
							chartLog(chartId, 'reconcile failed', { error: String(e) })
						}
					}

					// Paint the library's own chrome to match the surface.
					//
					// `paneProperties.background` colours the plot, but the
					// iframe's body and toolbars are the library's and default
					// to its dark grey (#2e2e2e). On /alkanes that grey sits
					// inside a #060510 surface with a purple cast, so the chart
					// reads as a lighter box dropped into the page.
					//
					// The iframe is same-origin, so the surface colour is
					// injected directly rather than guessed at in the static
					// stylesheet, which cannot know which surface a given chart
					// was mounted on.
					paintChrome()

					// Bars may have arrived before the chart was ready.
					fitToData()
					drawExecutions()

					chart
						.crossHairMoved?.()
						.subscribe?.(null, ({ price, time }: { price?: number; time?: number }) => {
							hoverRef.current?.(
								price != null && Number.isFinite(price)
									? formatAxisPrice(price, denom, unitFor(symbolRef.current))
									: null
							)
							// The band at this time: the latest point at or before
							// the cursor, which is the window that produced it.
							if (time != null && Number.isFinite(time)) {
								const points = trendPoints(symbolRef.current)
								let at: (typeof points)[number] | null = null
								for (const p of points) {
									if (p.time <= time) {
										at = p
									} else {
										break
									}
								}
								setBandAtCursor(at ? { fair: at.fair, p10: at.p10, p90: at.p90 } : null)
							} else {
								setBandAtCursor(null)
							}
						})

					chart.onIntervalChanged?.().subscribe?.(null, (interval: string) => {
						liveResolutionRef.current = interval
						rememberPreference('resolution', interval)
						resolutionChangeRef.current?.(interval)
						// A new width is a new series: the bars, prints and
						// fenced sales of the old one must not linger, or the
						// overlays snap to times that are no longer on the plot
						// and the geometry anchors on a bar of the wrong width.
						barsRef.current = []
						didFitRef.current = false
						outliersByKeyRef.current.clear()
						setOutliers([])
						hasOutliersRef.current = false
						printsByKeyRef.current.clear()
						setPrints([])
						hasPrintsRef.current = false
					})

					chart
						.onVisibleRangeChanged?.()
						.subscribe?.(null, ({ from, to }: { from: number; to: number }) => {
							const change = rangeChange(barsRef.current, from, to)
							if (change) {
								rangeRef.current?.(change.label, change.percent)
							}
							// Geometry is needed by anything positioned in time:
							// a page's own overlay, and the outlier dots, which
							// every collection can have without supplying one.
							if (
								overlayRef.current ||
								hasOutliersRef.current ||
								hasPrintsRef.current ||
								hasTrendRef.current ||
								hasThesesRef.current
							) {
								publishGeometry(from, to)
							}
						})

					// Track the plot every frame. The range subscription fires
					// for horizontal pans, and nothing fires for a drag on the
					// price axis, an autoscale nudge, or a resize -- the
					// candles move and an overlay keyed to events stays put.
					// So the overlay is keyed to the picture instead: sample
					// what the library would draw at a few fixed points and
					// republish geometry whenever any of them moved. When
					// nothing moves this costs a handful of getter calls.
					let lastSignature = ''
					let lastSize = ''
					const track = () => {
						if (cancelled) {
							return
						}
						trackerRef.current = requestAnimationFrame(track)
						if (
							!(
								overlayRef.current ||
								hasOutliersRef.current ||
								hasPrintsRef.current ||
								hasTrendRef.current ||
								hasThesesRef.current
							)
						) {
							return
						}
						try {
							const range = chart.getVisibleRange?.()
							const timeScale = chart.getTimeScale?.()
							const scale = chart.getPanes?.()?.[0]?.getMainSourcePriceScale?.()
							const canvas = container.current
								?.querySelector('iframe')
								?.contentDocument?.querySelector('canvas')
							const rect = canvas?.getBoundingClientRect()
							if (!range || !rect) {
								return
							}
							const size = `${rect.width}x${rect.height}`
							if (lastSize && size !== lastSize) {
								// Mid-resize. Hide the overlays until the plot
								// has held its size for a beat.
								if (!settlingRef.current) {
									settlingRef.current = true
									setSettling(true)
								}
								if (settleTimerRef.current !== null) {
									window.clearTimeout(settleTimerRef.current)
								}
								settleTimerRef.current = window.setTimeout(() => {
									settleTimerRef.current = null
									settlingRef.current = false
									setSettling(false)
									lastSignature = ''
								}, 160)
							}
							lastSize = size
							const signature = [
								range.from,
								range.to,
								timeScale?.barSpacing(),
								timeScale?.rightOffset(),
								timeScale?.width(),
								scale?.priceToCoordinate(1),
								scale?.priceToCoordinate(1_000_000),
								rect.left,
								rect.top,
								size,
								barsRef.current.length
							].join('|')
							if (signature !== lastSignature) {
								lastSignature = signature
								publishGeometry(range.from, range.to)
							}
						} catch {
							// Mid-rebuild; the next frame will have it.
						}
					}
					trackerRef.current = requestAnimationFrame(track)
				})
			})
			.catch((e: Error) => {
				if (!cancelled) {
					setError(e.message)
				}
			})

		return () => {
			cancelled = true
			chartLog(chartId, 'teardown')
			try {
				widgetRef.current?.remove()
			} catch {
				// The widget throws if it is torn down mid-initialisation;
				// nothing useful can be done about it and the container is
				// discarded either way.
			}
			widgetRef.current = null
			redrawExecutionsRef.current = null
			publishGeometryRef.current = null
			if (trackerRef.current !== null) {
				cancelAnimationFrame(trackerRef.current)
				trackerRef.current = null
			}
			if (settleTimerRef.current !== null) {
				window.clearTimeout(settleTimerRef.current)
				settleTimerRef.current = null
			}
			settlingRef.current = false
			execShapesRef.current = []
			chartReadyRef.current = false
			// Drop this chart's bands. The store is module-level so that the
			// datafeed can write to it without a React path; unmounting is the
			// one moment we know nothing will read them again.
			for (const seen of symbolsSeen) {
				clearTrend(seen)
				forgetUnit(seen)
			}
			symbolsSeen.clear()
			setGeometry(null)
		}
		// `symbol` covers id/kind/denom; theme and resolution rebuild the widget
		// because TradingView applies neither reliably after construction.
		// `hasBars` rather than `bars`: the widget is rebuilt when a caller
		// switches into or out of supplying its own series, not every time that
		// series ticks.
		// Rebuild ONLY on what genuinely needs a new widget.
		//
		// Theme and compact change the library's own construction options, and
		// switching a page into or out of supplying its own bars swaps the
		// datafeed — none of those can be applied in place. Everything else,
		// symbol and resolution included, is applied to the live widget by the
		// effect below.
		//
		// What used to be here — `denoms`, `denomLabels`, `getMarks`, `title`,
		// `symbol`, `denom`, `resolution` — meant a fresh array or object
		// literal from ANY parent render tore the widget down and rebuilt it.
		// Rebuilding sets state, which re-renders, which rebuilds: a remount
		// loop that looked like a slow chart.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [resolvedTheme, compact, hasBars, stableGetMarks, chartId])

	// Symbol and resolution change IN PLACE.
	//
	// `setSymbol` keeps the widget, its drawings and — the part users feel —
	// the viewport. Flipping SATS to USD used to rebuild the chart and throw
	// away whatever range had been scrolled to.
	useEffect(() => {
		const widget = widgetRef.current
		if (!widget?.setSymbol || !chartReadyRef.current) {
			// Nothing to do before the first chart is ready: it is constructed
			// with the current symbol already.
			return
		}
		// The live resolution, so a denomination change keeps the candle width
		// the viewer chose.
		const keepResolution = liveResolutionRef.current || resolution
		chartLog(chartId, 'setSymbol (no rebuild)', { symbol, resolution: keepResolution })
		// A new symbol means a different series, so the bars and the one-shot
		// viewport fit both start over.
		barsRef.current = []
		didFitRef.current = false
		outliersByKeyRef.current.clear()
		setOutliers([])
		hasOutliersRef.current = false
		printsByKeyRef.current.clear()
		setPrints([])
		hasPrintsRef.current = false
		// The band store is keyed by symbol and merged across history pages, so
		// a switch needs no invalidation: the new symbol reads its own bucket
		// and the old one is dropped when this chart unmounts. Clearing here
		// would only throw away a cache that is still correct.
		try {
			widget.setSymbol(symbol, keepResolution, () => {
				chartLog(chartId, 'symbol applied', { symbol, resolution: keepResolution })
			})
		} catch (e) {
			chartLog(chartId, 'setSymbol failed', { error: String(e) })
		}
		// Deliberately not keyed on `resolution`: the prop is the opening
		// value, and re-running on it would undo a viewer's toolbar choice.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [symbol, chartId])

	useDepChangeLog(chartId, 'render', {
		symbol,
		resolution,
		denoms,
		denomLabels,
		getMarks,
		overlay,
		bars,
		executions,
		title,
		compact,
		resolvedTheme
	})

	return (
		<div className={classNames('relative flex h-full w-full flex-col', className)}>
			{/* In the full layout the denomination switcher lives in the chart's
			    own header, beside the resolution tabs. Compact embeds disable
			    that header, so they fall back to a strip above the plot. */}
			{compact && (denoms.length > 1 || title) && (
				<div className="flex items-center justify-between gap-3 px-1 pb-2">
					{title && <h2 className="truncate text-sm font-medium">{title}</h2>}
					{compact && denoms.length > 1 && (
						<div
							role="group"
							aria-label="Price denomination"
							className="flex items-center gap-1 rounded-md bg-neutral-100 p-0.5 dark:bg-neutral-900"
						>
							{denoms.map((d) => (
								<button
									key={d}
									type="button"
									onClick={() => {
										setDenom(d)
										rememberPreference('denom', d)
										onDenomChange?.(d)
									}}
									aria-pressed={denom === d}
									className={classNames(
										'rounded px-2 py-1 text-xs font-medium transition-colors',
										denom === d
											? 'bg-white text-black shadow-sm dark:bg-neutral-700 dark:text-white'
											: 'text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-200'
									)}
								>
									{denomLabels?.[d] ?? DENOM_LABELS[d]}
								</button>
							))}
						</div>
					)}
				</div>
			)}

			{degraded && denom === 'sats' && (
				<p className="px-1 pb-2 text-xs text-amber-600 dark:text-amber-500">
					Showing a price line: this collection&apos;s full candle history is not available yet.
				</p>
			)}

			{error ? (
				<div className="flex h-full min-h-[320px] items-center justify-center text-sm text-neutral-500">
					Couldn&apos;t load the chart.
				</div>
			) : (
				<>
					{/* A dollar or market-cap series cannot be reconstructed from a
					    BTC-denominated line without inventing a bitcoin price
					    history, so say that plainly rather than letting the
					    library show a bare "no data" that reads as a bug. */}
					{degraded && denom !== 'sats' && (
						<div className="pointer-events-none absolute z-10 flex h-full w-full items-center justify-center p-6">
							<p className="max-w-xs text-center text-sm text-neutral-500">
								{denom === 'mcap' ? 'Market cap' : 'Dollar'} history needs the full candle series,
								which isn&apos;t available for this collection yet. Switch to SATS to see its price.
							</p>
						</div>
					)}
					<div id={domId} ref={container} className="relative min-h-[320px] flex-1">
						{/* Under the dots: the band is context, the dots are
						    events. `bandRev` is the store's revision, which
						    starts at zero and moves on every write -- it is
						    what makes a module-level store a render
						    dependency. */}
						{showBand && geometry && bandRev > 0 && !settling && (
							<FairBand geometry={geometry} symbol={symbol} denom={denom} />
						)}
						{showPrints && geometry && (outliers.length > 0 || prints.length > 0) && !settling && (
							<PrintDots
								geometry={geometry}
								prints={prints}
								outliers={outliers}
								denom={denom}
								unit={unitFor(symbol)}
								fungible={kind === 'collection' && isFungible(id)}
								btcUsd={btcUsd}
								onZoom={(from, to) => {
									void widgetRef.current?.activeChart?.().setVisibleRange?.({ from, to })
								}}
								bucketSecs={resolutionSeconds(liveResolutionRef.current || resolution)}
								fairAt={(time) => {
									let at: number | null = null
									for (const p of trendPoints(symbol)) {
										if (p.time <= time) {
											at = p.fair
										} else {
											break
										}
									}
									return at
								}}
								onHover={hover}
								onOpen={(id) => router.push(`/inscription/${id}`)}
							/>
						)}
						{showTheses && geometry && theses && theses.length > 0 && !settling && (
							<ThesisFaces
								geometry={geometry}
								theses={theses}
								bars={barsRef.current.map((b) => ({
									time: Math.floor(b.time / 1000),
									close: b.close
								}))}
								onHover={hover}
							/>
						)}
						{showBand && geometry && bandAtCursor && (
							<div
								className="pointer-events-none absolute z-[6] rounded bg-background/80 px-1.5 py-0.5 text-[11px] tabular-nums text-muted-foreground backdrop-blur-sm"
								style={{ left: geometry.plot.left + 8, top: geometry.plot.top + 64 }}
							>
								<span className="text-[var(--primary-500)]">Band</span>{' '}
								{formatAxisLabel(bandAtCursor.p10, denom, unitFor(symbol))}
								{' – '}
								{formatAxisLabel(bandAtCursor.p90, denom, unitFor(symbol))}
								{' · fair '}
								<span className="text-foreground">
									{formatAxisLabel(bandAtCursor.fair, denom, unitFor(symbol))}
								</span>
							</div>
						)}
						{hoverCard && geometry && (
							<HoverCardView
								card={hoverCard}
								plot={geometry.plot}
								width={container.current?.clientWidth ?? 0}
								onEnter={cancelHoverClose}
								onLeave={() => hover(null)}
							/>
						)}
						{violations.length > 0 && (
							<div
								role="status"
								title={violations.map((v) => `${v.rule} at ${v.at}: ${v.detail}`).join('\n')}
								className="pointer-events-auto absolute right-16 top-12 z-20 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[11px] font-medium text-amber-600 dark:text-amber-400"
							>
								Some bars failed validation ({violations.map((v) => v.rule).join(', ')})
							</div>
						)}
						{overlay && geometry && (
							<div className="pointer-events-none absolute inset-0 z-10" aria-label="Chart overlay">
								{overlay(geometry)}
							</div>
						)}
					</div>
					{/* The overlay switches, under the chart, the same strip the
					    alkanes page uses. Collections only: a market chart has
					    none of these, and alkanes runs its own copy of the strip
					    with its own overlays. */}
					{kind === 'collection' && !compact && (
						<ChartOverlayToggles
							items={[
								{
									key: 'band',
									label: 'Band',
									hint: OVERLAY_HINTS.band,
									checked: showBand,
									onChange: (on) => {
										setShowBand(on)
										rememberPreference('band', on ? '1' : '0')
									},
									testId: 'chart-overlay-band'
								},
								{
									key: 'sales',
									label: 'Sales',
									hint: OVERLAY_HINTS.sales,
									checked: showPrints,
									// What is on screen, not what is cached.
									count: geometry
										? [...prints, ...outliers].filter((o) => geometry.timeToX(o.time) !== null)
												.length
										: prints.length + outliers.length,
									onChange: (on) => {
										setShowPrints(on)
										rememberPreference('prints', on ? '1' : '0')
									},
									testId: 'chart-overlay-sales'
								},
								{
									key: 'theses',
									label: 'Thesis',
									hint: OVERLAY_HINTS.theses,
									checked: showTheses,
									count: theses?.length ?? 0,
									disabled: !theses || theses.length === 0,
									reason: 'No thesis posts yet',
									onChange: (on) => {
										setShowTheses(on)
										rememberPreference('theses', on ? '1' : '0')
									},
									testId: 'chart-overlay-thesis'
								}
							]}
						/>
					)}
				</>
			)}
		</div>
	)
}

export { buildSymbol, withDenom }
export type { ChartKind, Denom, ChartMark }
